import * as InactivityActions from "actions/InactivityActions";
import * as ModalActions from "actions/ModalActions";
import * as PlaybackStateActions from "actions/PlaybackStateActions";
import getAdPlaying from "selectors/ads/getAdPlaying";
import getChapterTypeDisplayName from "selectors/chapter/getChapterTypeDisplayName";
import getPlaybackState from "selectors/chapter/getPlaybackState";
import getShowBackButton from "selectors/navigation/getShowBackButton";
import getShowNavChapterProgressText from "selectors/breakpoint/getShowNavChapterProgressText";
import getShowTitleInNavOnChapterCover from "selectors/breakpoint/getShowTitleInNavOnChapterCover";
import getStorySharingUrl from "selectors/story/getStorySharingUrl";
import getUseFlattenedInterviewQuestionsList from "selectors/breakpoint/getUseFlattenedInterviewQuestionsList";
import getUseSwipeableInterview from "selectors/breakpoint/getUseSwipeableInterview";
import NavigationComponent from "components/Navigation";
import NavigationWrapper from "utils/NavigationWrapper";
import React from "react";
import { autobind } from "core-decorators";
import { compose, withHandlers, onlyUpdateForPropTypes } from "recompose";
import { connect } from "react-redux";
import { getOptionalChapterEntity } from "selectors/chapter/makeGetChapterEntity";
import { SLIDESHOW } from "utils/ChapterType";

// Create redux-wrapped Navigation component instance
const connector = connect(
  (state, ownProps) => {
    const actualStateKey = ownProps.chapterId
      ? `chapter-${ownProps.chapterId}`
      : "story";
    const adPlaying = getAdPlaying(state, ownProps);
    const playbackState = getPlaybackState(state, {
      stateKey: actualStateKey,
    });
    const currentChapter = getOptionalChapterEntity(state, ownProps);
    const hideCaptions = state.playerOptions.hideDefaultCaptions !== true;

    return {
      adPlaying,
      currentChapter,
      chapterListVisible: state.modals.chapterListVisible,
      currentChapterTypeDisplayName: getChapterTypeDisplayName(state, ownProps),
      forcingChapterCover: state.forceChapterCover,
      isShowingSlideshowCaptions: playbackState.slideshowCaptionsShowing,
      onHomeClick: () =>
        NavigationWrapper.push("story", state.route.params.storySlug),
      playing: playbackState.playing,
      routePrefix: state.route.params.routePrefix,
      router: state.route,
      storySlug: state.route.params.storySlug,
      showChapterProgress:
        getShowNavChapterProgressText(state, ownProps) &&
        state.playerOptions.hideChapterProgress !== true,
      showingStoryCover:
        !state.route.params.chapterKey &&
        !state.playerOptions.singleVideoPlayer,
      showBackButton: getShowBackButton(state, {
        ...ownProps,
        stateKey: actualStateKey,
      }),
      showHamburger:
        !adPlaying && state.story.customizations.playerNavigation.showHamburger,
      showShareButton:
        !adPlaying &&
        state.story.customizations.playerNavigation.allowSharing !== false,
      showSlideshowControls:
        currentChapter &&
        currentChapter.type === SLIDESHOW &&
        !state.forceChapterCover &&
        !!hideCaptions,
      shareURL: getStorySharingUrl(state, ownProps),
      showStoryTitle: !state.story.hasCustomMonogram,
      showTitleInNavOnChapterCover: getShowTitleInNavOnChapterCover(
        state,
        ownProps,
      ),
      slideshowGalleryToggle: !adPlaying && state.modals.slideshowGalleryToggle,
      slideshowInfoToggle: !adPlaying && state.modals.slideshowInfoToggle,
      stateKey: actualStateKey,
      shareUrl: getStorySharingUrl(state, ownProps),
      useFlattenedInterviewQuestionsList: getUseFlattenedInterviewQuestionsList(
        state,
        ownProps,
      ),
      useSwipeableInterview: getUseSwipeableInterview(state, ownProps),
    };
  },
  (dispatch, ownProps) => ({
    onModalClose: (stateKey, { transitioning } = { transitioning: false }) => {
      if (
        (ownProps.playingPreModal || stateKey === "story") &&
        !transitioning
      ) {
        dispatch(
          PlaybackStateActions.changePlaybackState(
            { stateKey },
            {
              playing: true,
            },
          ),
        );
      }
      // this needs to happen every time so the chapter doesn't get stuck
      dispatch(
        PlaybackStateActions.changePlaybackState(
          { stateKey },
          {
            userInitiatedPause: false,
          },
        ),
      );
    },
    onModalOpen: (stateKey, playing) => {
      ownProps.onModalOpen({ playing });
      dispatch(
        PlaybackStateActions.changePlaybackState(
          { stateKey },
          {
            userInitiatedPause: true,
            playing: false,
          },
        ),
      );
    },
    onReportActivity: () => dispatch(InactivityActions.reportActivity()),
    onReportUIActivity: () => dispatch(InactivityActions.reportUIActivity()),
    setChapterListVisible: (visible) =>
      dispatch(ModalActions.setChapterListVisible(visible)),
    setSlideshowGalleryToggle: (visible) =>
      dispatch(ModalActions.setSlideshowGalleryToggle(visible)),
    setSlideshowInfoToggle: (visible) =>
      dispatch(ModalActions.setSlideshowInfoToggle(visible)),
  }),
  (stateProps, dispatchProps, ownProps) => {
    const mergedProps = {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
    };

    return {
      ...mergedProps,
      onModalClose: (options) => {
        dispatchProps.onReportUIActivity();
        dispatchProps.onModalClose(stateProps.stateKey, options);
      },
      onModalOpen: () => {
        dispatchProps.onReportUIActivity();
        dispatchProps.onModalOpen(stateProps.stateKey, stateProps.playing);
      },
      showSlideshowControls:
        mergedProps.showSlideshowControls && !mergedProps.adPlaying,
    };
  },
);

const Navigation = compose(
  connector,
  withHandlers({
    onReportActivity: (props) => props.onReportActivity,
    onReportUIActivity: (props) => props.onReportUIActivity,
    onHomeClick: (props) => props.onHomeClick,
    onModalClose: (props) => props.onModalClose,
    onModalOpen: (props) => props.onModalOpen,
  }),
  onlyUpdateForPropTypes,
)(NavigationComponent);

export default class Container extends React.PureComponent {
  static displayName = "Container(Navigation)";

  state = {
    wasPlayingPreModal: false,
  };

  @autobind
  handleModalOpen({ playing: wasPlayingPreModal }) {
    this.setState({
      wasPlayingPreModal,
    });
  }

  render() {
    return (
      <Navigation
        {...this.props}
        onModalOpen={this.handleModalOpen}
        playingPreModal={this.state.wasPlayingPreModal}
      />
    );
  }
}
